"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useAssistantUsage = void 0;
const react_1 = require("react");
const assistant_credits_1 = __importDefault(require("@app/request/assistant-credits"));
const util_1 = require("@app/util");
const PERCENT_MIN = 0;
const PERCENT_MAX = 100;
/**
 * Asks once per conversation, and again when the workspace changes or an exchange completes.
 *
 * Reads the credit ledger rather than the deprecated `assistantUsage` facade, whose `resetsAt`
 * no longer describes anything real: credits do not come back at the start of a month, each
 * package lapses on its own date and a top-up raises the ceiling mid-month.
 *
 * The `sessionId` goes to the server, which answers `blocked` for THAT conversation: a session
 * already charged is never blocked, so a balance that runs out mid-conversation cannot cut it
 * off, and the frontend does not have to remember whether the session was paid for. A new
 * session is exactly when the answer can change from "you may send" to "you may not". Within
 * one conversation the figures only move when a colleague spends, which the manager cannot act
 * on, so a slightly stale count inside a live conversation is accepted rather than polled for.
 *
 * `undefined` means "no usage data": in flight, no workspace, or the request failed. Every
 * surface treats that as "show nothing and restrict nothing".
 */
const useAssistantUsage = (workspaceId, sessionId) => {
    const [credits, setCredits] = (0, react_1.useState)(undefined);
    const [reloadToken, setReloadToken] = (0, react_1.useState)(0);
    const refresh = (0, react_1.useCallback)(() => setReloadToken((token) => token + 1), []);
    // Dropped on a workspace change and nowhere else: a refresh must not blank the ring it was
    // asked to update, and a new session on the same workspace is looking at the same balance.
    (0, react_1.useEffect)(() => {
        setCredits(undefined);
    }, [workspaceId]);
    (0, react_1.useEffect)(() => {
        if (!(0, util_1.isValue)(workspaceId)) {
            setCredits(undefined);
            return;
        }
        // A workspace or session change remounts nothing here, so the in-flight answer for the one
        // we just left must not land in state.
        let isCurrent = true;
        void (0, assistant_credits_1.default)({
            workspace: workspaceId,
            sessionId: (0, util_1.isValue)(sessionId) ? sessionId : undefined,
        })
            // A TRANSPORT failure rejects rather than resolving with `errors`, so `processErrors` in
            // the request never sees it — an environment that does not serve the field answers 400,
            // and Apollo turns that into a thrown ApolloError. Caught here rather than there because
            // this is the expected-error branch, and the branch is "no usage data": the sidebar shows
            // no ring and restricts nothing. Without this the rejection is unhandled.
            .catch(() => undefined)
            .then((result) => {
            if (isCurrent)
                setCredits(result);
        });
        return () => {
            isCurrent = false;
        };
    }, [workspaceId, sessionId, reloadToken]);
    const usage = (0, react_1.useMemo)(() => {
        if (!(0, util_1.isValue)(credits))
            return undefined;
        const { conversationsGranted, conversationsRemaining, blocked, blockedReason, nearestExpiry, } = credits;
        // Derived from the two figures the server already floored, so `used + remaining` is exactly
        // `limit` and the panel's pair of numbers cannot disagree with the ring drawn between them.
        const used = Math.max(0, conversationsGranted - conversationsRemaining);
        const common = { used, blocked, blockedReason, nearestExpiry };
        // Nothing granted is not "uncapped": it is a workspace holding no package, which a ring
        // cannot express — the banner above the composer explains it instead.
        if (conversationsGranted === 0) {
            return { ...common, hasLimit: false, limit: null, usedPercent: PERCENT_MIN };
        }
        // Rounded here rather than through `numberUtil.calculatePercentage`, whose own `round`
        // branch is unreachable.
        const raw = Math.round((used / conversationsGranted) * PERCENT_MAX);
        const usedPercent = Math.min(PERCENT_MAX, Math.max(PERCENT_MIN, raw));
        return { ...common, hasLimit: true, limit: conversationsGranted, usedPercent };
    }, [credits]);
    return { usage, refresh };
};
exports.useAssistantUsage = useAssistantUsage;
exports.default = exports.useAssistantUsage;
